import { AxiosError } from 'axios';
import { useRef, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router';
import type { SignInCredential, AuthResult, User, Token, SignInError } from '@/@types/auth';
import { REDIRECT_URL_KEY } from '@/constants/app.constant';
import appConfig from '@/configs/app.config';
import { extractFirstTenantId } from '@/utils/jwt';
import { apiSignIn, apiSignOut } from '@/services/AuthService.ts';
import { useSessionUser, useToken } from '@/store/authStore';
import AuthContext from './AuthContext';
import type { ReactNode, Ref } from 'react';
import type { NavigateFunction } from 'react-router';

type AuthProviderProps = { children: ReactNode };

export type IsolatedNavigatorRef = {
  navigate: NavigateFunction;
};

const IsolatedNavigator = ({ ref }: { ref: Ref<IsolatedNavigatorRef> }) => {
  const navigate = useNavigate();

  useImperativeHandle(ref, () => {
    return {
      navigate,
    };
  }, [navigate]);

  return <></>;
};

function AuthProvider({ children }: AuthProviderProps) {
  const signedIn = useSessionUser((state) => state.session.signedIn);
  const user = useSessionUser((state) => state.user);
  const setUser = useSessionUser((state) => state.setUser);
  const setSessionSignedIn = useSessionUser((state) => state.setSessionSignedIn);
  const setTenantId = useSessionUser((state) => state.setTenantId);

  const { token, setToken } = useToken();
  const [tokenState, setTokenState] = useState(token);

  const authenticated = Boolean(tokenState && signedIn);

  const navigatorRef = useRef<IsolatedNavigatorRef>(null);

  const redirect = () => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const redirectUrl = params.get(REDIRECT_URL_KEY);

    navigatorRef.current?.navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
  };

  const handleSignIn = (tokens: Token, user?: User) => {
    setToken(tokens.accessToken);
    setTokenState(tokens.accessToken);
    setSessionSignedIn(true);

    const tenantId = extractFirstTenantId(tokens.accessToken);
    setTenantId(tenantId || '');

    if (user) {
      setUser(user);
    }
  };

  const handleSignOut = () => {
    setToken('');
    setUser(null);
    setSessionSignedIn(false);
    setTenantId('');
  };

  const signIn = async (values: SignInCredential): AuthResult => {
    try {
      const resp = await apiSignIn(values);

      if (resp) {
        handleSignIn({ accessToken: resp.access_token }, resp.user);
        redirect();
        return {
          status: 'success',
          message: '',
        };
      }
      return {
        status: 'failed',
        message: 'Unable to sign in',
      };
    } catch (e) {
      const error = e as AxiosError<SignInError>;

      return {
        status: 'failed',
        message: error?.response?.data?.detail?.message || error.toString(),
      };
    }
  };

  const signOut = async () => {
    try {
      await apiSignOut();
    } finally {
      handleSignOut();
      navigatorRef.current?.navigate('/');
    }
  };

  return (
    <AuthContext
      value={{
        authenticated,
        user,
        signIn,
        signOut,
      }}
    >
      {children}
      <IsolatedNavigator ref={navigatorRef} />
    </AuthContext>
  );
}

export default AuthProvider;
